/**
 * ============================================================================
 * transactionService.ts - ENTERPRISE API VERSION
 * ============================================================================
 * 
 * This service handles all transaction operations through secure server API.
 * Direct Firestore writes are NO LONGER ALLOWED under enterprise security rules.
 * ============================================================================
 */

import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { API_BASE } from '../utils/apiBase';

// ============================================================================
// API UTILITIES
// ============================================================================

async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall<T>(endpoint: string, options: {
  method?: 'GET' | 'POST';
  body?: object;
  params?: Record<string, string>;
}): Promise<T> {
  const token = await getAuthToken();
  
  let url = `${API_BASE}${endpoint}`;
  if (options.params) {
    const searchParams = new URLSearchParams(options.params);
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    method: options.method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `API call failed: ${response.status}`);
  }
  
  return data;
}

// ============================================================================
// TYPES
// ============================================================================

export type TransactionType = 
  | 'deposit' 
  | 'withdrawal' 
  | 'table_join' 
  | 'table_leave'
  | 'rebuy' 
  | 'win' 
  | 'loss' 
  | 'rake' 
  | 'bonus' 
  | 'transfer'
  | 'purchase'
  | 'redemption'
  | 'refund'
  | 'adjustment';

export type Currency = 'playcoins' | 'pkcash';

export interface Transaction {
  id: string;
  odD: string;
  type: TransactionType;
  amount: number;
  currency: Currency;
  description?: string;
  metadata?: Record<string, any>;
  relatedId?: string;
  status: 'pending' | 'completed' | 'failed';
  timestamp: Date | null;
  createdAt: Date | null;
}

export interface CreateTransactionParams {
  type: TransactionType;
  amount: number;
  currency: Currency;
  description?: string;
  metadata?: Record<string, any>;
  relatedId?: string;
  idempotencyKey?: string; // Optional - auto-generated if not provided
}

// ============================================================================
// IDEMPOTENCY UTILITIES
// ============================================================================

/**
 * Generate a deterministic idempotency key based on transaction details
 * This ensures the same logical transaction always produces the same key
 */
function generateIdempotencyKey(params: CreateTransactionParams, userId: string): string {
  const timestamp = Math.floor(Date.now() / 1000); // Second-level granularity
  const components = [
    userId,
    params.type,
    params.amount.toFixed(2),
    params.currency,
    params.relatedId || 'none',
    params.description?.slice(0, 50) || 'none',
    timestamp
  ];
  return components.join('|');
}

// Track pending requests to prevent duplicate calls
const pendingTransactions = new Map<string, Promise<{ success: boolean; transactionId: string }>>();

// Recent transaction cache for deduplication (5 minute window)
const recentTransactions = new Map<string, { transactionId: string; timestamp: number }>();
const TRANSACTION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function cleanupRecentTransactions(): void {
  const now = Date.now();
  for (const [key, value] of recentTransactions) {
    if (now - value.timestamp > TRANSACTION_CACHE_TTL) {
      recentTransactions.delete(key);
    }
  }
}

export interface PlaythroughStatus {
  odD: string;
  totalDeposits: number;
  currentPlaythrough: number;
  requiredPlaythrough: number;
  isComplete: boolean;
}

// ============================================================================
// TRANSACTION SERVICE CLASS
// ============================================================================

class TransactionService {
  /**
   * Create a new transaction record via server API
   *  Now with idempotency protection:

   * - Generates deterministic idempotency keys
   * - Prevents duplicate in-flight requests
   * - Caches recent transactions to detect retries
   */
  async createTransaction(params: CreateTransactionParams): Promise<{ 
    success: boolean; 
    transactionId: string;
  }> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    // Generate idempotency key if not provided
    const idempotencyKey = params.idempotencyKey || generateIdempotencyKey(params, user.uid);
    
    console.log(`💳 Creating ${params.type} transaction: ${params.amount} ${params.currency} [key: ${idempotencyKey.slice(0, 20)}...]`);
    
    // Cleanup old cached transactions
    cleanupRecentTransactions();
    
    // Check if this transaction was recently completed (dedup)
    const cached = recentTransactions.get(idempotencyKey);
    if (cached) {
      console.log(`⚡ Transaction already completed, returning cached result: ${cached.transactionId}`);
      return { success: true, transactionId: cached.transactionId };
    }
    
    // Check if this exact request is already in flight (prevent duplicate calls)
    const pending = pendingTransactions.get(idempotencyKey);
    if (pending) {
      console.log(`⏳ Duplicate request detected, waiting for pending transaction...`);
      return pending;
    }
    
    // Create the promise and track it
    const transactionPromise = (async () => {
      try {
        const result = await apiCall<{ success: boolean; transactionId: string }>(
          '/recordTransaction',
          { 
            method: 'POST', 
            body: { ...params, idempotencyKey } 
          }
        );

        // Cache successful transaction
        recentTransactions.set(idempotencyKey, {
          transactionId: result.transactionId,
          timestamp: Date.now()
        });

        console.log(`✅ Transaction created: ${result.transactionId}`);
        return result;
      } finally {
        // Always remove from pending after completion
        pendingTransactions.delete(idempotencyKey);
      }
    })();
    
    // Track the pending request
    pendingTransactions.set(idempotencyKey, transactionPromise);
    
    return transactionPromise;
  }
  
  /**
   * Create transaction with explicit idempotency key
   * Use this when you need deterministic deduplication across sessions
   */
  async createTransactionWithKey(
    params: CreateTransactionParams, 
    idempotencyKey: string
  ): Promise<{ success: boolean; transactionId: string }> {
    return this.createTransaction({ ...params, idempotencyKey });
  }

  /**
   * Get current user's transaction history via server API
   */
  async getMyTransactions(options?: {
    limit?: number;
    currency?: Currency;
  }): Promise<Transaction[]> {
    const params: Record<string, string> = {};
    if (options?.limit) params.limit = options.limit.toString();
    if (options?.currency) params.currency = options.currency;

    const result = await apiCall<{ success: boolean; transactions: Transaction[] }>(
      '/getMyTransactions',
      { method: 'GET', params }
    );

    return result.transactions;
  }

  /**
   * Get transaction history (alias)
   */
  async getTransactionHistory(options?: {
    limit?: number;
    currency?: Currency;
  }): Promise<Transaction[]> {
    return this.getMyTransactions(options);
  }

  /**
   * Get transactions for a user
   */
  async getTransactions(userId: string, limitCount: number = 20): Promise<Transaction[]> {
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.uid === userId) {
      return this.getMyTransactions({ limit: limitCount });
    }
    console.warn('Cannot fetch other users transactions');
    return [];
  }

  /**
   * Get transactions for a specific table
   */
  async getTableTransactions(tableId: string): Promise<Transaction[]> {
    const allTransactions = await this.getMyTransactions({ limit: 100 });
    return allTransactions.filter(tx => 
      tx.relatedId === tableId || tx.metadata?.tableId === tableId
    );
  }

  /**
   * Get playthrough status
   */
  async getPlaythroughStatus(userId: string): Promise<PlaythroughStatus> {
    const transactions = await this.getMyTransactions({ limit: 100 });
    
    const totalDeposits = transactions
      .filter(t => t.type === 'deposit' || t.type === 'purchase')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const currentPlaythrough = transactions
      .filter(t => ['table_join', 'win', 'loss'].includes(t.type))
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const requiredPlaythrough = totalDeposits * 1;
    
    return {
      odD: userId,
      totalDeposits,
      currentPlaythrough,
      requiredPlaythrough,
      isComplete: currentPlaythrough >= requiredPlaythrough,
    };
  }

  /**
   * Validate withdrawal eligibility
   */
  async validateWithdrawal(userId: string, amount: number): Promise<{
    canWithdraw: boolean;
    reason?: string;
    maxAmount?: number;
  }> {
    const playthrough = await this.getPlaythroughStatus(userId);
    
    if (!playthrough.isComplete) {
      return {
        canWithdraw: false,
        reason: `Playthrough not met. Need ${playthrough.requiredPlaythrough - playthrough.currentPlaythrough} more.`,
        maxAmount: 0,
      };
    }
    
    return { canWithdraw: true, maxAmount: amount };
  }

  /**
   * Create withdrawal request
   */
  async createWithdrawalRequest(
    userId: string,
    amount: number,
    currency: Currency,
    method: string
  ): Promise<{ success: boolean; requestId?: string; error?: string }> {
    try {
      //  FIX: Validate amount is a number

      if (isNaN(amount) || amount <= 0) {
        return { success: false, error: 'Invalid withdrawal amount' };
      }
      
      const result = await this.createTransaction({
        type: 'withdrawal',
        amount: -Math.abs(amount),
        currency: currency, //  FIX: Use passed currency instead of hardcoded 'pkcash'

        description: `Withdrawal via ${method}`,
        metadata: { method, currency },
      });
      return { success: true, requestId: result.transactionId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get withdrawable amount
   */
  async getWithdrawableAmount(userId: string): Promise<number> {
    const validation = await this.validateWithdrawal(userId, Infinity);
    return validation.maxAmount || 0;
  }

  /**
   * Record table join
   */
  async recordTableJoin(tableId: string, amount: number, currency: Currency): Promise<string> {
    const result = await this.createTransaction({
      type: 'table_join',
      amount: -amount,
      currency,
      description: `Joined table ${tableId}`,
      relatedId: tableId,
      metadata: { tableId }
    });
    return result.transactionId;
  }

  /**
   * Record table leave
   */
  async recordTableLeave(tableId: string, amount: number, currency: Currency): Promise<string> {
    const result = await this.createTransaction({
      type: 'table_leave',
      amount: amount,
      currency,
      description: `Left table ${tableId}`,
      relatedId: tableId,
      metadata: { tableId }
    });
    return result.transactionId;
  }

  /**
   * Record table buy-in (alias for recordTableJoin)
   */
  async recordTableBuyIn(
    userId: string,
    currency: Currency,
    amount: number,
    tableId: string
  ): Promise<void> {
    await this.recordTableJoin(tableId, amount, currency);
  }

  /**
   * Record table cash-out (alias for recordTableLeave)
   */
  async recordTableCashOut(
    userId: string,
    currency: Currency,
    amount: number,
    tableId: string
  ): Promise<void> {
    await this.recordTableLeave(tableId, amount, currency);
  }

  /**
   * Record a win
   */
  async recordWin(tableId: string, amount: number, currency: Currency, handNumber?: number): Promise<string> {
    const result = await this.createTransaction({
      type: 'win',
      amount,
      currency,
      description: `Won hand${handNumber ? ` #${handNumber}` : ''} at table ${tableId}`,
      relatedId: tableId,
      metadata: { tableId, handNumber }
    });
    return result.transactionId;
  }

  /**
   * Record game win (alias)
   */
  async recordGameWin(
    userId: string,
    currency: Currency,
    amount: number,
    tableId: string
  ): Promise<void> {
    await this.recordWin(tableId, amount, currency);
  }

  /**
   * Record a loss
   */
  async recordLoss(tableId: string, amount: number, currency: Currency, handNumber?: number): Promise<string> {
    const result = await this.createTransaction({
      type: 'loss',
      amount: -Math.abs(amount),
      currency,
      description: `Lost hand${handNumber ? ` #${handNumber}` : ''} at table ${tableId}`,
      relatedId: tableId,
      metadata: { tableId, handNumber }
    });
    return result.transactionId;
  }

  /**
   * Record game loss (alias)
   */
  async recordGameLoss(
    userId: string,
    currency: Currency,
    amount: number,
    tableId: string
  ): Promise<void> {
    await this.recordLoss(tableId, amount, currency);
  }

  /**
   * Record purchase
   */
  async recordPurchase(
    userId: string,
    currency: Currency,
    amount: number,
    description: string
  ): Promise<string> {
    const result = await this.createTransaction({
      type: 'purchase',
      amount: Math.abs(amount),
      currency,
      description,
    });
    return result.transactionId;
  }

  /**
   * Record rake earned (for agents)
   */
  async recordRakeEarned(
    userId: string,
    amount: number,
    tableId: string
  ): Promise<void> {
    await this.createTransaction({
      type: 'rake',
      amount,
      currency: 'pkcash',
      description: `Rake from table ${tableId}`,
      relatedId: tableId,
    });
  }

  /**
   * Get balance history summary
   */
  async getBalanceHistory(currency: Currency): Promise<{
    totalDeposits: number;
    totalWithdrawals: number;
    totalWins: number;
    totalLosses: number;
    netChange: number;
  }> {
    const transactions = await this.getMyTransactions({ currency, limit: 100 });
    
    let totalDeposits = 0;
    let totalWithdrawals = 0;
    let totalWins = 0;
    let totalLosses = 0;

    for (const tx of transactions) {
      switch (tx.type) {
        case 'deposit':
        case 'purchase':
        case 'bonus':
          totalDeposits += Math.abs(tx.amount);
          break;
        case 'withdrawal':
        case 'redemption':
          totalWithdrawals += Math.abs(tx.amount);
          break;
        case 'win':
        case 'table_leave':
          if (tx.amount > 0) totalWins += tx.amount;
          break;
        case 'loss':
        case 'table_join':
          if (tx.amount < 0) totalLosses += Math.abs(tx.amount);
          break;
      }
    }

    return {
      totalDeposits,
      totalWithdrawals,
      totalWins,
      totalLosses,
      netChange: totalDeposits - totalWithdrawals + totalWins - totalLosses
    };
  }

  /**
   * Create agent payout request
   *  ENTERPRISE: Calls backend API to deduct from wallet.rakeBalance
   * Paid to the USDC wallet or PayPal contact the agent saved in the Wallet
   * (same destination record player redemptions use).
   */
  async createAgentPayoutRequest(
    agentId: string,
    amount: number,
    payoutMethod: 'usdc' | 'paypal'
  ): Promise<{ success: boolean; requestId?: string; error?: string }> {
    try {
      //  ENTERPRISE: Call dedicated agent payout endpoint

      // This deducts from rakeBalance, not pkCash
      const result = await apiCall<{
        success: boolean;
        requestId: string;
        amount: number;
        status: string;
      }>(
        '/requestAgentPayout',
        {
          method: 'POST',
          body: { amount, payoutMethod }
        }
      );
      return { success: true, requestId: result.requestId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get agent payout status
   *  ENTERPRISE: Now reads from wallet.rakeBalance (protected agent commissions)

   */
  async getAgentPayoutStatus(agentId: string): Promise<{
    pendingAmount: number;
    completedAmount: number;
    availableAmount: number;
    canRequestPayout: boolean;
    lastPayoutDate: Date | null;
    nextPayoutDate: Date;
    message?: string;
    recentPayouts: Transaction[];
  }> {
    try {
      //  ENTERPRISE: Get available balance from wallet.rakeBalance

      // This is the protected agent commission balance that cannot be gambled
      const walletDoc = await getDoc(doc(db, 'wallets', agentId));
      let availableAmount = 0;
      
      if (walletDoc.exists()) {
        const walletData = walletDoc.data();
        // rakeBalance is the actual available amount for payout
        availableAmount = walletData.rakeBalance || 0;
      }
      
      // Get all agent payouts to show history and pending amounts
      const payoutsQuery = query(
        collection(db, 'agentPayouts'),
        where('agentId', '==', agentId)
      );
      const payoutsSnapshot = await getDocs(payoutsQuery);
      
      let pendingAmount = 0;
      let completedAmount = 0;
      let lastPayoutDate: Date | null = null;
      const recentPayouts: Transaction[] = [];
      
      payoutsSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.status === 'pending' || data.status === 'pending_review' || data.status === 'approved' || data.status === 'processing') {
          pendingAmount += data.amount || 0;
        } else if (data.status === 'completed' || data.status === 'paid') {
          completedAmount += data.amount || 0;
          // Track last payout date
          const payoutDate = data.completedAt?.toDate() || data.createdAt?.toDate();
          if (payoutDate && (!lastPayoutDate || payoutDate > lastPayoutDate)) {
            lastPayoutDate = payoutDate;
          }
        }
        recentPayouts.push({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Transaction);
      });
      
      //  ENTERPRISE: Available amount is directly from rakeBalance

      // No need to calculate from totalEarned - completedAmount anymore
      // The backend manages rakeBalance correctly
      
      // Minimum payout threshold ($10)
      const MIN_PAYOUT = 10;
      const canRequestPayout = availableAmount >= MIN_PAYOUT;
      
      // Next payout date (1st of next month)
      const now = new Date();
      const nextPayoutDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      let message: string | undefined;
      if (availableAmount < MIN_PAYOUT && availableAmount > 0) {
        message = `Minimum payout is $${MIN_PAYOUT}. You need $${(MIN_PAYOUT - availableAmount).toFixed(2)} more.`;
      }
      
      return {
        pendingAmount,
        completedAmount,
        availableAmount,
        canRequestPayout,
        lastPayoutDate,
        nextPayoutDate,
        message,
        recentPayouts: recentPayouts.slice(0, 10),
      };
    } catch (error) {
      console.error('Error getting agent payout status:', error);
      return {
        pendingAmount: 0,
        completedAmount: 0,
        availableAmount: 0,
        canRequestPayout: false,
        lastPayoutDate: null,
        nextPayoutDate: new Date(),
        message: 'Error loading payout status',
        recentPayouts: [],
      };
    }
  }

  // ==========================================================================
  // AUDIT & RETENTION METHODS
  // ==========================================================================

  /**
   * Get transactions for a specific date range (for audits/tax)
   */
  async getTransactionsForDateRange(options: {
    startDate: Date;
    endDate: Date;
    currency?: Currency;
    limit?: number;
  }): Promise<Transaction[]> {
    try {
      const params: Record<string, string> = {
        startDate: options.startDate.toISOString(),
        endDate: options.endDate.toISOString(),
      };
      if (options.currency) params.currency = options.currency;
      if (options.limit) params.limit = options.limit.toString();

      const response = await apiCall<{ success: boolean; transactions: Transaction[] }>(
        '/getTransactionsForDateRange',
        { method: 'GET', params }
      );
      return response.transactions || [];
    } catch (error) {
      console.error('Failed to get transactions for date range:', error);
      return [];
    }
  }

  /**
   * Get annual transaction summary for tax reporting
   */
  async getAnnualSummary(year: number): Promise<{
    year: number;
    pkcash: {
      totalDeposits: number;
      totalWithdrawals: number;
      totalWins: number;
      totalLosses: number;
      totalRake: number;
      totalBonuses: number;
      netIncome: number;
    };
    playcoins: {
      totalDeposits: number;
      totalBonuses: number;
      totalWins: number;
      totalLosses: number;
    };
    transactionCount: number;
  }> {
    try {
      const response = await apiCall<any>('/getAnnualTransactionSummary', {
        method: 'GET',
        params: { year: year.toString() },
      });
      return {
        year: response.year,
        pkcash: response.pkcash,
        playcoins: response.playcoins,
        transactionCount: response.transactionCount,
      };
    } catch (error) {
      console.error('Failed to get annual summary:', error);
      return {
        year,
        pkcash: {
          totalDeposits: 0,
          totalWithdrawals: 0,
          totalWins: 0,
          totalLosses: 0,
          totalRake: 0,
          totalBonuses: 0,
          netIncome: 0,
        },
        playcoins: {
          totalDeposits: 0,
          totalBonuses: 0,
          totalWins: 0,
          totalLosses: 0,
        },
        transactionCount: 0,
      };
    }
  }

  /**
   * Export transactions for download (CSV format data)
   */
  async exportTransactions(options: {
    startDate?: Date;
    endDate?: Date;
    currency?: Currency;
    format?: 'csv' | 'json';
  }): Promise<{
    data: string;
    filename: string;
    mimeType: string;
  }> {
    const startDate = options.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
    const endDate = options.endDate || new Date();
    
    const transactions = await this.getTransactionsForDateRange({
      startDate,
      endDate,
      currency: options.currency,
      limit: 1000,
    });

    if (options.format === 'json') {
      return {
        data: JSON.stringify(transactions, null, 2),
        filename: `transactions_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}.json`,
        mimeType: 'application/json',
      };
    }

    // CSV format
    const headers = ['Date', 'Type', 'Amount', 'Currency', 'Description', 'Status', 'Transaction ID'];
    const rows = transactions.map(tx => [
      tx.timestamp?.toISOString?.() || tx.createdAt?.toISOString?.() || '',
      tx.type,
      tx.amount.toFixed(2),
      tx.currency,
      tx.description || '',
      tx.status,
      tx.id,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    return {
      data: csvContent,
      filename: `transactions_${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}.csv`,
      mimeType: 'text/csv',
    };
  }
}

// ============================================================================
// RETENTION CONSTANTS
// ============================================================================

export const TRANSACTION_RETENTION = {
  PKCASH_RETENTION_YEARS: 7,        // 7 years for PKCash (IRS requirement)
  PLAYCOINS_RETENTION_DAYS: 90,     // 90 days for PlayCoins
  MAX_DISPLAY_LIMIT: 500,
};

// ============================================================================
// EXPORT
// ============================================================================

const transactionService = new TransactionService();
export default transactionService;
export { transactionService };